import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './RegisterWithOTP.css';

function RegisterWithOTP() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '+91',
        password: ''
    });
    
    const [step, setStep] = useState('form'); // 'form', 'phone-otp', 'email-otp', 'complete'
    const [phoneOtp, setPhoneOtp] = useState('');
    const [emailOtp, setEmailOtp] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [otpTimers, setOtpTimers] = useState({ phone: 0, email: 0 });
    
    const navigate = useNavigate();

    const validateForm = () => {
        if (!formData.name.trim()) {
            setError('Name is required');
            return false;
        }
        if (!formData.email.trim() || !isValidEmail(formData.email)) {
            setError('Please enter a valid email address');
            return false;
        }
        if (!formData.phone.trim() || !isValidPhone(formData.phone)) {
            setError('Please enter a valid phone number starting with +91');
            return false;
        }
        if (!formData.password || formData.password.length < 6) {
            setError('Password must be at least 6 characters');
            return false;
        }
        return true;
    };

    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const isValidPhone = (phone) => {
        // Must start with +91 and have 10 digits after that
        const phoneRegex = /^\+91[6-9]\d{9}$/;
        return phoneRegex.test(phone);
    };

    const startOtpTimer = (type) => {
        setOtpTimers(prev => ({ ...prev, [type]: 300 })); // 5 minutes
        const interval = setInterval(() => {
            setOtpTimers(prev => {
                const newTime = prev[type] - 1;
                if (newTime <= 0) {
                    clearInterval(interval);
                    return { ...prev, [type]: 0 };
                }
                return { ...prev, [type]: newTime };
            });
        }, 1000);
    };

    const formatTimer = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handlePhoneKeyDown = (e) => {
        const { selectionStart, selectionEnd, value } = e.target;
        
        // Prevent deletion of +91 prefix
        if ((e.key === 'Backspace' || e.key === 'Delete') && 
            selectionStart <= 3 && selectionEnd <= 3 && value.startsWith('+91')) {
            e.preventDefault();
        }
        
        // Set cursor position after +91 if user clicks at the beginning
        if (e.key === 'ArrowLeft' && selectionStart <= 3) {
            setTimeout(() => {
                e.target.setSelectionRange(3, 3);
            }, 0);
        }
    };

    const handlePhoneFocus = (e) => {
        // Set cursor position after +91 when field is focused
        setTimeout(() => {
            if (e.target.value === '+91') {
                e.target.setSelectionRange(3, 3);
            }
        }, 0);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // Auto-format phone number
        if (name === 'phone') {
            let formattedValue = value;
            
            // If user tries to delete +91 prefix, maintain it
            if (!formattedValue.startsWith('+91')) {
                // Extract only digits from the input
                const digitsOnly = formattedValue.replace(/\D/g, '');
                
                // If it starts with 91, prepend +
                if (digitsOnly.startsWith('91')) {
                    formattedValue = '+' + digitsOnly;
                } 
                // If it starts with valid mobile digits (6-9), prepend +91
                else if (digitsOnly.length > 0 && ['6', '7', '8', '9'].includes(digitsOnly[0])) {
                    formattedValue = '+91' + digitsOnly;
                }
                // Otherwise maintain +91 prefix
                else {
                    formattedValue = '+91';
                }
            } else {
                // Clean the part after +91 to keep only digits
                const prefix = '+91';
                const restOfNumber = formattedValue.substring(3).replace(/\D/g, '');
                formattedValue = prefix + restOfNumber;
            }
            
            // Limit to +91 + 10 digits maximum
            if (formattedValue.length > 13) {
                formattedValue = formattedValue.substring(0, 13);
            }
            
            setFormData(prev => ({ ...prev, [name]: formattedValue }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        setLoading(true);
        setError('');
        
        try {
            // Send phone OTP first
            const phoneOtpResponse = await fetch('/api/auth/send-otp', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ phone: formData.phone })
            });

            const phoneOtpData = await phoneOtpResponse.json();
            
            if (phoneOtpData.success) {
                setStep('phone-otp');
                setMessage('OTP sent to your phone number. Please verify.');
                startOtpTimer('phone');
            } else {
                setError(phoneOtpData.message || 'Failed to send phone OTP');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handlePhoneOtpVerify = async (e) => {
        e.preventDefault();
        
        if (!phoneOtp || phoneOtp.length !== 6) {
            setError('Please enter a valid 6-digit OTP');
            return;
        }

        setLoading(true);
        setError('');
        
        try {
            const response = await fetch('/api/auth/verify-otp', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    phone: formData.phone,
                    otp: phoneOtp
                })
            });

            const data = await response.json();
            
            if (data.success) {
                // Phone verified, now send email OTP
                const emailOtpResponse = await fetch('/api/auth/send-email-otp', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ email: formData.email })
                });

                const emailOtpData = await emailOtpResponse.json();
                
                if (emailOtpData.success) {
                    setStep('email-otp');
                    setMessage('Phone verified! OTP sent to your email. Please verify.');
                    startOtpTimer('email');
                } else {
                    setError(emailOtpData.message || 'Failed to send email OTP');
                }
            } else {
                setError(data.message || 'Invalid phone OTP');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleEmailOtpVerify = async (e) => {
        e.preventDefault();
        
        if (!emailOtp || emailOtp.length !== 6) {
            setError('Please enter a valid 6-digit OTP');
            return;
        }

        setLoading(true);
        setError('');
        
        try {
            const response = await fetch('/api/auth/verify-email-otp', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    email: formData.email,
                    otp: emailOtp
                })
            });

            const data = await response.json();
            
            if (data.success) {
                // Both OTPs verified, now register the user
                const registerResponse = await fetch('/api/auth/register', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(formData)
                });

                const registerData = await registerResponse.json();
                
                if (registerData.token) {
                    setStep('complete');
                    setMessage('Registration successful! Redirecting to login...');
                    setTimeout(() => {
                        navigate('/login');
                    }, 2000);
                } else {
                    setError(registerData.message || 'Registration failed');
                }
            } else {
                setError(data.message || 'Invalid email OTP');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const resendPhoneOtp = async () => {
        if (otpTimers.phone > 0) return;
        
        setLoading(true);
        try {
            const response = await fetch('/api/auth/send-otp', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ phone: formData.phone })
            });

            const data = await response.json();
            if (data.success) {
                setMessage('Phone OTP resent successfully');
                startOtpTimer('phone');
            } else {
                setError(data.message || 'Failed to resend phone OTP');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const resendEmailOtp = async () => {
        if (otpTimers.email > 0) return;
        
        setLoading(true);
        try {
            const response = await fetch('/api/auth/send-email-otp', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email: formData.email })
            });

            const data = await response.json();
            if (data.success) {
                setMessage('Email OTP resent successfully');
                startOtpTimer('email');
            } else {
                setError(data.message || 'Failed to resend email OTP');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const renderFormStep = () => (
        <form onSubmit={handleSubmit} className="register-form">
            <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your full name"
                    tabIndex={1}
                />
            </div>

            <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your email address"
                    tabIndex={2}
                />
            </div>

            <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    onKeyDown={handlePhoneKeyDown}
                    onFocus={handlePhoneFocus}
                    required
                    placeholder="Phone number (e.g., +919876543210)"
                    tabIndex={3}
                />
                <small className="help-text">Phone number must start with +91</small>
            </div>

            <div className="form-group">
                <label htmlFor="password">Password *</label>
                <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your password (min 6 characters)"
                    tabIndex={4}
                />
            </div>

            <button type="submit" disabled={loading} className="register-btn" tabIndex={5}>
                {loading ? 'Processing...' : 'Verify Phone & Email'}
            </button>
        </form>
    );

    const renderPhoneOtpStep = () => (
        <form onSubmit={handlePhoneOtpVerify} className="otp-form">
            <div className="otp-header">
                <h3>📱 Verify Phone Number</h3>
                <p>We've sent a 6-digit OTP to <strong>{formData.phone}</strong></p>
            </div>

            <div className="form-group">
                <label htmlFor="phoneOtp">Enter Phone OTP *</label>
                <input
                    type="text"
                    id="phoneOtp"
                    value={phoneOtp}
                    onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').substring(0, 6);
                        setPhoneOtp(value);
                        setError('');
                    }}
                    required
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                    className="otp-input"
                    tabIndex={1}
                    autoFocus
                />
            </div>

            <div className="otp-actions">
                <button type="submit" disabled={loading || phoneOtp.length !== 6} className="verify-btn" tabIndex={2}>
                    {loading ? 'Verifying...' : 'Verify Phone'}
                </button>
                
                {otpTimers.phone > 0 ? (
                    <p className="timer">Resend OTP in {formatTimer(otpTimers.phone)}</p>
                ) : (
                    <button type="button" onClick={resendPhoneOtp} disabled={loading} className="resend-btn" tabIndex={3}>
                        Resend Phone OTP
                    </button>
                )}
            </div>

            <button type="button" onClick={() => setStep('form')} className="back-btn" tabIndex={4}>
                ← Back to Form
            </button>
        </form>
    );

    const renderEmailOtpStep = () => (
        <form onSubmit={handleEmailOtpVerify} className="otp-form">
            <div className="otp-header">
                <h3>📧 Verify Email Address</h3>
                <p>We've sent a 6-digit OTP to <strong>{formData.email}</strong></p>
            </div>

            <div className="form-group">
                <label htmlFor="emailOtp">Enter Email OTP *</label>
                <input
                    type="text"
                    id="emailOtp"
                    value={emailOtp}
                    onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').substring(0, 6);
                        setEmailOtp(value);
                        setError('');
                    }}
                    required
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                    className="otp-input"
                    tabIndex={1}
                    autoFocus
                />
            </div>

            <div className="otp-actions">
                <button type="submit" disabled={loading || emailOtp.length !== 6} className="verify-btn" tabIndex={2}>
                    {loading ? 'Verifying...' : 'Verify Email & Register'}
                </button>
                
                {otpTimers.email > 0 ? (
                    <p className="timer">Resend OTP in {formatTimer(otpTimers.email)}</p>
                ) : (
                    <button type="button" onClick={resendEmailOtp} disabled={loading} className="resend-btn" tabIndex={3}>
                        Resend Email OTP
                    </button>
                )}
            </div>

            <button type="button" onClick={() => setStep('phone-otp')} className="back-btn" tabIndex={4}>
                ← Back to Phone Verification
            </button>
        </form>
    );

    const renderCompleteStep = () => (
        <div className="success-container">
            <div className="success-icon">✅</div>
            <h3>Registration Successful!</h3>
            <p>Your account has been created successfully. Redirecting to login...</p>
        </div>
    );

    return (
        <div className="register-container">
            <div className="register-card">
                <div className="register-header">
                    <h2>Create Account</h2>
                    <div className="step-indicator">
                        <div className={`step ${step === 'form' ? 'active' : step !== 'form' ? 'completed' : ''}`}>1</div>
                        <div className={`step ${step === 'phone-otp' ? 'active' : (step === 'email-otp' || step === 'complete') ? 'completed' : ''}`}>2</div>
                        <div className={`step ${step === 'email-otp' ? 'active' : step === 'complete' ? 'completed' : ''}`}>3</div>
                        <div className={`step ${step === 'complete' ? 'active' : ''}`}>4</div>
                    </div>
                    <div className="step-labels">
                        <span>Form</span>
                        <span>Phone</span>
                        <span>Email</span>
                        <span>Done</span>
                    </div>
                </div>

                {error && <div className="error-message">{error}</div>}
                {message && <div className="success-message-text">{message}</div>}

                {step === 'form' && renderFormStep()}
                {step === 'phone-otp' && renderPhoneOtpStep()}
                {step === 'email-otp' && renderEmailOtpStep()}
                {step === 'complete' && renderCompleteStep()}

                <div className="register-footer">
                    <p>Already have an account? <Link to="/login">Login here</Link></p>
                </div>
            </div>
        </div>
    );
}

export default RegisterWithOTP;
